import { NavLink, useNavigate } from 'react-router-dom';
import { ChevronDown, LogOut } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { useAuth } from '../../context/AuthContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_bc39e05f-6f0c-4b7d-bcf6-5fdd53257229/artifacts/5l4govpy_Nuera%20Logo%202.png";

const navLinks = [
  { to: '/marketing', label: 'Marketing' },
  { to: '/ad-performance', label: 'Ad Performance' },
  { to: '/website', label: 'Website' },
  { to: '/', label: 'Home' },
  { to: '/competitor', label: 'Competitor' },
  { to: '/sales', label: 'Sales' },
  { to: '/insights', label: 'Insights' },
];

export const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header 
      data-testid="header"
      className="sticky top-0 z-50 w-full border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60"
    >
      <div className="container mx-auto flex h-16 items-center justify-between px-4 max-w-7xl">
        {/* Logo */}
        <NavLink to="/" className="flex items-center" data-testid="logo-link">
          <img 
            src={LOGO_URL} 
            alt="Nuera" 
            className="h-8 w-auto"
            data-testid="logo-image"
          />
        </NavLink>

        {/* Navigation */}
        <nav className="hidden md:flex items-center gap-1 bg-slate-100/80 rounded-full px-2 py-1 relative z-40" data-testid="main-nav">
          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
              className={({ isActive }) =>
                `px-4 py-2 text-sm font-medium rounded-full transition-all relative z-50 ${
                  isActive
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-sm'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/50'
                }`
              }
              end={link.to === '/'}
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        {/* User Profile */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button 
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
              data-testid="user-menu-trigger"
            >
              <Avatar className="h-9 w-9 border-2 border-purple-100">
                <AvatarImage 
                  src="https://images.unsplash.com/photo-1615843423179-bea071facf96?w=100&h=100&fit=crop" 
                  alt={user?.name || 'User'}
                />
                <AvatarFallback className="bg-gradient-to-br from-purple-500 to-pink-500 text-white">
                  {user?.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              <span className="hidden md:block text-sm font-medium text-slate-700">
                {user?.name || 'User'}
              </span>
              <ChevronDown className="h-4 w-4 text-slate-500" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48" data-testid="user-menu">
            <div className="px-2 py-1.5">
              <p className="text-sm font-medium text-slate-800">{user?.name || 'User'}</p>
              <p className="text-xs text-slate-500">{user?.username || 'admin'}</p>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="menu-profile">Profile</DropdownMenuItem>
            <DropdownMenuItem data-testid="menu-settings">Settings</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={handleLogout}
              className="text-red-600 focus:text-red-600 focus:bg-red-50"
              data-testid="menu-logout"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

export default Header;
